import { persistStateTypes } from 'a/reducers/persistStateReducer';
import {
  GAME_ID,
  INIT_TRAY,
  RELOAD_TRAY,
  ADD_TILE_TO_DESK,
  REMOVE_TILE_FROM_DESK,
  REORDER_TILES_ON_DESK,
  CUT_TILE,
  SERVE_ANSWER,
  SERVE_PRACTICE_ANSWER,
  TURN_SECOND_ATTEMPT_MODE_ON,
  TURN_TRY_AGAIN_MODE_ON,
  TURN_FINAL_FEEDBACK_MODE_ON,
  DROP_ZONE_KEYS,
} from '../data/goblinGourmetConstants';

import * as Helper from './goblinGourmetQuestionReducerHelper';

const initialState = {
  // target word as it is
  targetWord: null,
  // question id, always remains as it is
  questionId: null,
  // uuid for EM
  uuid: null,
  // array with current tiles
  tiles: [],
  // array with initial tiles
  initialCollection: [],
  // array with ids of tray tiles
  tray: [],
  // array with ids of desk tiles
  desk: [],
  // total amount of remaining cut attempts
  cutAttemptsRemainder: 0,
  // total amount of used cut attempts
  cutAttemptsUsed: 0,
  // user's answer, becomes non-null once user pressed Serve button and cut desk tiles got joined into one tile
  answer: null,
  // becomes true/false once user pressed Serve button and cut desk tiles got joined into one tile
  isAnswerCorrect: null,
  // true when second attempt mode is active
  secondAttemptModeOn: false,
  // true when stry again mode is active
  tryAgainModeOn: false,
  // becomes true, once correct answer appears as tile on the desk
  finalFeedbackModeOn: false,
  // amount of actions user performed so far (cuts, tiles moved to/from tray to/from desk)
  actionsAmount: 0,
  // audio keys
  audios: [],
};

// most complicated reducer: everything related to question data: trays, tiles, etc
const goblinGourmetQuestionReducer = (state = initialState, action) => {
  if (action.gameId !== GAME_ID) {
    return state;
  }

  switch (action.type) {
    case persistStateTypes.REQUESTRESPONSE_GAMESTATE: {
      // if (action.payload.question !== undefined) {
      //   return {
      //     ...action.payload.standardRound,
      //   };
      // }
      return state;
    }
    case INIT_TRAY: {
      const {
        audios, cutsNeeded, questionId, uuid, targetWord, tiles,
      } = action.payload;

      // distribute tiles between trays
      const currentTiles = Helper.getDistributedTray(tiles.map(Helper.getTile));

      return {
        ...initialState,
        targetWord,
        questionId,
        uuid,
        tiles: currentTiles,
        initialCollection: tiles,
        tray: currentTiles.map(tile => tile.id),
        cutAttemptsRemainder: cutsNeeded + 2,
        // no used attempts so far
        cutAttemptsUsed: 0,
        audios,
      };
    }
    case RELOAD_TRAY: {
      const { cutsNeeded, tiles } = action.payload;

      const currentTiles = Helper.getDistributedTray(tiles.map(Helper.getTile));

      return {
        ...state,
        tiles: currentTiles,
        tray: currentTiles.map(tile => tile.id),
        desk: [],
        // restore remainder
        cutAttemptsRemainder: cutsNeeded + 2,
        // restore to 0
        cutAttemptsUsed: 0,
        answer: null,
        isAnswerCorrect: null,
        secondAttemptModeOn: state.secondAttemptModeOn,
        tryAgainModeOn: false,
        actionsAmount: state.actionsAmount + 1,
      };
    }
    case ADD_TILE_TO_DESK: {
      const { tileId } = action.payload;

      return {
        ...state,
        desk: [...state.desk, tileId],
        tray: state.tray.filter(id => id !== tileId),
        actionsAmount: state.actionsAmount + 1,
      };
    }
    case REMOVE_TILE_FROM_DESK: {
      const { tileId } = action.payload;

      let { dropZoneKey } = action.payload;

      // get weight of left/right trays
      const [leftTrayWeight, rightTrayWeight] = Helper.getTrayPartitionWeight(state.tiles, state.tray);

      if (dropZoneKey === DROP_ZONE_KEYS.LEFT_TRAY && leftTrayWeight - rightTrayWeight > 7) {
        // if user dropped tile into left tray and left tray is overloaded, change ot right
        dropZoneKey = DROP_ZONE_KEYS.RIGHT_TRAY;
      } else if (dropZoneKey === DROP_ZONE_KEYS.RIGHT_TRAY && rightTrayWeight - leftTrayWeight > 7) {
        // if user dropped tile into right tray and right tray is overloaded, change ot left
        dropZoneKey = DROP_ZONE_KEYS.LEFT_TRAY;
      } else {
        // and if user dropped tile somewhere else, pick the easiest tray
        dropZoneKey = Helper.getEasiestDropZoneKey(state.tiles, state.tray);
      }

      return {
        ...state,
        tiles: state.tiles.map(tile => ({
          ...tile,
          dropZoneKey: tile.id === tileId ? dropZoneKey : tile.dropZoneKey,
        })),
        tray: [...state.tray, tileId],
        desk: state.desk.filter(id => id !== tileId),
        actionsAmount: state.actionsAmount + 1,
      };
    }
    case REORDER_TILES_ON_DESK: {
      const { desk } = action.payload;

      return {
        ...state,
        desk,
        actionsAmount: state.actionsAmount + 1,
      };
    }
    case CUT_TILE: {
      const { tile, slices } = action.payload;

      // remove tile we cut from tiles, because it won't exist anymore
      const tiles = state.tiles.filter(oldTile => oldTile.id !== tile.id);

      // remember where it was on desk
      const deskTileToCutIndex = state.desk.findIndex(tileId => tileId === tile.id);

      // get text of new left and righ tiles
      const [leftTileText, rightTileText] = slices;

      // and then then generate tiles themselves, also mark them as moved to desk (because they're on the desk once cut)
      const leftTile = {
        ...Helper.getTile(leftTileText),
        dropZoneKey: Helper.getEasiestDropZoneKey(tiles, state.tray),
      };
      tiles.push(leftTile);

      const rightTile = {
        ...Helper.getTile(rightTileText),
        dropZoneKey: Helper.getEasiestDropZoneKey(tiles, state.tray),
      };
      tiles.push(rightTile);

      // add them into desk
      const desk = [
        // pick tiles before the cut one
        ...state.desk.slice(0, deskTileToCutIndex),
        leftTile.id,
        rightTile.id,
        // pick tiles after the cut one
        ...state.desk.slice(deskTileToCutIndex + 1),
      ];

      return {
        ...state,
        tiles,
        desk,
        // decrease remainder, cause we performed cut
        cutAttemptsRemainder: state.cutAttemptsRemainder - 1,
        // increases used attempts, cause we performed cut
        cutAttemptsUsed: state.cutAttemptsUsed + 1,
        actionsAmount: state.actionsAmount + 1,
      };
    }
    case SERVE_ANSWER: {
      const { deskTiles } = action.payload;

      const answer = deskTiles.reduce(
        (text, tile) => text + tile.text,
        '',
      );

      return {
        ...state,
        answer,
        isAnswerCorrect: answer === state.targetWord,
      };
    }
    case SERVE_PRACTICE_ANSWER: {
      const { deskTiles, targetTiles } = action.payload;

      const answer = deskTiles.map(tile => tile.text).join('-');

      return {
        ...state,
        answer,
        isAnswerCorrect: answer === targetTiles.join('-'),
      };
    }
    case TURN_SECOND_ATTEMPT_MODE_ON: {
      const { deskTiles } = action.payload;

      const { tray, desk } = Helper.getSecondAttemptTiles(state, deskTiles);

      return {
        ...state,
        tray,
        desk,
        secondAttemptModeOn: true,
        answer: null,
        isAnswerCorrect: null,
      };
    }
    case TURN_TRY_AGAIN_MODE_ON: {
      return {
        ...state,
        tryAgainModeOn: true,
        secondAttemptModeOn: false,
      };
    }
    case TURN_FINAL_FEEDBACK_MODE_ON: {
      const targetWordTile = Helper.getTile(state.targetWord);

      return {
        ...state,
        tiles: [targetWordTile],
        tray: [targetWordTile.id],
        desk: [targetWordTile.id],
        finalFeedbackModeOn: true,
      };
    }
    default:
      return state;
  }
};

export default goblinGourmetQuestionReducer;
